import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { MdAdd } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { adminSelectStyles } from "../Models/admin-modal-theme";
import { studentPalette } from "../../constants/studentPalette";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";
import {
  createTagExamRequest,
  getStudentTagsRequest,
} from "../../modules/students-modules/student-exams/Actions";
import { studentSolveExamPath } from "../../utils/studentExamPaths";

const QUESTION_COUNTS = [10, 30, 50];

const CreateExamMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tags = useSelector((state) => state.studentExams.tags);
  const tagsLoading = useSelector((state) => state.studentExams.tagsLoading);
  const isCreating = useSelector((state) => state.studentExams.isCreating);

  const [selectedTags, setSelectedTags] = useState([]);
  const [questionNumber, setQuestionNumber] = useState(10);

  useEffect(() => {
    dispatch(getStudentTagsRequest());
  }, [dispatch]);

  const tagOptions = useMemo(
    () =>
      (Array.isArray(tags) ? tags : []).map((tag) => ({
        value: tag.id,
        label: tag.name,
      })),
    [tags]
  );

  const createExam = () => {
    const tagIds = (selectedTags || [])
      .map((tag) => tag?.value)
      .filter((id) => id != null);
    if (tagIds.length === 0) {
      toast.error("Select at least one tag.");
      return;
    }
    dispatch(
      createTagExamRequest(
        {
          tag_ids: tagIds,
          question_number: questionNumber,
        },
        ({ exam }) => {
          setSelectedTags([]);
          navigate(studentSolveExamPath(exam.id, exam.name));
        }
      )
    );
  };

  return (
    <Box
      w="100%"
      bg={studentSurfaces.raised}
      borderWidth="1px"
      borderColor={studentSurfaces.border}
      borderRadius="2xl"
      p={{ base: 4, md: 5 }}
      boxShadow="0 8px 28px rgba(91, 13, 22, 0.08)"
    >
      <VStack align="stretch" spacing={4}>
        <Box>
          <Text
            fontWeight="800"
            fontSize="md"
            color={studentPalette.primary}
            sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
          >
            Create a practice exam
          </Text>
          <Text fontSize="sm" color={studentSurfaces.textMuted} mt={1}>
            Choose tags and a question count, then start.
          </Text>
        </Box>

        <Box>
          <Text fontSize="xs" fontWeight="800" color={studentSurfaces.textMuted} mb={2} textTransform="uppercase" letterSpacing="0.12em">
            Tags
          </Text>
          <Select
            options={tagOptions}
            isMulti
            isLoading={tagsLoading}
            value={selectedTags}
            onChange={(data) => setSelectedTags(data || [])}
            styles={adminSelectStyles()}
            placeholder="Select tags"
            noOptionsMessage={() => (tagsLoading ? "Loading tags…" : "No tags available")}
          />
        </Box>

        <Box>
          <Text fontSize="xs" fontWeight="800" color={studentSurfaces.textMuted} mb={2} textTransform="uppercase" letterSpacing="0.12em">
            Number of questions
          </Text>
          <HStack spacing={3} flexWrap="wrap">
            {QUESTION_COUNTS.map((count) => {
              const selected = questionNumber === count;
              return (
                <Button
                  key={count}
                  type="button"
                  minW="72px"
                  borderRadius="xl"
                  fontWeight="800"
                  bg={selected ? studentPalette.primary : "transparent"}
                  color={selected ? studentPalette.cream : studentPalette.primary}
                  borderWidth="2px"
                  borderColor={studentPalette.primary}
                  _hover={{
                    bg: selected ? studentPalette.secondary : "rgba(91, 13, 22, 0.08)",
                  }}
                  sx={{
                    "html[data-theme='dark'] &": {
                      color: selected ? studentPalette.cream : "var(--text-primary)",
                      borderColor: selected ? studentPalette.primary : "var(--border-subtle)",
                    },
                  }}
                  onClick={() => setQuestionNumber(count)}
                >
                  {count}
                </Button>
              );
            })}
          </HStack>
        </Box>

        <Flex justify="flex-end">
          <Button
            type="button"
            px={5}
            bg={studentPalette.primary}
            color={studentPalette.cream}
            fontWeight="800"
            borderRadius="xl"
            leftIcon={<MdAdd size={20} />}
            isLoading={isCreating}
            onClick={createExam}
            _hover={{ bg: studentPalette.secondary }}
          >
            Start exam
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
};

export default CreateExamMenu;
